// src/pages/DataSourcesPage.jsx
import { useEffect, useState } from "react";
import { apiGet, apiPost } from "../api/client";
import { SkeletonList } from "../../shared/ui";
import "../styles/DataSourcesPage.css";

const emptyForm = {
  name: "",
  db_type: "mysql",
  host: "127.0.0.1",
  port: 3306,
  database: "",
  username: "",
  password: "",
};

export default function DataSourcesPage() {
  const [dataSources, setDataSources] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [testingConnection, setTestingConnection] = useState(null);

  const loadDataSources = () => {
    setLoading(true);
    apiGet("/datasources/")
      .then(setDataSources)
      .catch((e) => console.error(e))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadDataSources();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Special handling when db_type changes (set sensible default ports/host)
    if (name === "db_type") {
      setForm((prev) => {
        let next = { ...prev, db_type: value };

        if (value === "mysql") {
          next.port = 3306;
          if (!prev.host || prev.host === "https://yourtenant.atlassian.net") {
            next.host = "127.0.0.1";
          }
        } else if (value === "postgres") {
          next.port = 5432;
          if (!prev.host || prev.host === "https://yourtenant.atlassian.net") {
            next.host = "127.0.0.1";
          }
        } else if (value === "sqlite") {
          next.port = "";
          if (!prev.database) {
            next.database = "local.db";
          }
        } else if (value === "jira") {
          next.port = 443;
          if (!prev.host || prev.host === "127.0.0.1") {
            next.host = "https://yourtenant.atlassian.net";
          }
          if (!prev.database) {
            next.database = "jira";
          }
        } else if (value === "azure_devops") {
          next.port = 443;
          if (!prev.host || prev.host === "127.0.0.1") {
            next.host = "your-org";
          }
          if (!prev.database) {
            next.database = "YourProject";
          }
        }

        return next;
      });
      return;
    }

    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    try {
      await apiPost("/datasources/", {
        ...form,
        port: form.port === "" ? null : Number(form.port),
      });
      setForm(emptyForm);
      loadDataSources();
      setMessage("✅ Data source created");
    } catch (err) {
      setMessage(`❌ Error: ${err.message || String(err)}`);
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async (id) => {
    setTestingConnection(id);
    setMessage("");
    try {
      // If backend returns 200, apiGet resolves → treat as OK
      await apiGet(`/datasources/${id}/test-connection`);
      setMessage("✅ Connection OK");
    } catch (err) {
      setMessage(`❌ Connection failed: ${err.message || String(err)}`);
    } finally {
      setTestingConnection(null);
    }
  };

  const getDbIcon = (dbType) => {
    const icons = {
      mysql: "🐬",
      postgres: "🐘",
      sqlite: "🔵",
      jira: "🧩",
      azure_devops: "🔷",
      mongodb: "🍃",
      redis: "🔴",
    };
    return icons[dbType] || "💾";
  };

  const formatDbTypeLabel = (dbType) => {
    switch (dbType) {
      case "mysql":
        return "MySQL";
      case "postgres":
        return "PostgreSQL";
      case "sqlite":
        return "SQLite";
      case "jira":
        return "Jira Cloud";
      case "azure_devops":
        return "Azure DevOps";
      default:
        return dbType;
    }
  };

  // Dynamic labels depending on type
  const hostLabel = (() => {
    switch (form.db_type) {
      case "sqlite":
        return "Host (unused)";
      case "jira":
        return "Base URL (Jira)";
      case "azure_devops":
        return "Organization";
      default:
        return "Host";
    }
  })();

  const databaseLabel = (() => {
    switch (form.db_type) {
      case "sqlite":
        return "Database (file path)";
      case "jira":
        return "Database (label)";
      case "azure_devops":
        return "Project";
      default:
        return "Database";
    }
  })();

  const usernameLabel = (() => {
    switch (form.db_type) {
      case "jira":
        return "Username / Jira Email";
      case "azure_devops":
        return "Username (optional)";
      default:
        return "Username";
    }
  })();

  const passwordLabel = (() => {
    switch (form.db_type) {
      case "jira":
        return "API Token";
      case "azure_devops":
        return "PAT (Personal Access Token)";
      default:
        return "Password";
    }
  })();

  const isSqlite = form.db_type === "sqlite";
  const isJira = form.db_type === "jira";
  const isAdo = form.db_type === "azure_devops";

  return (
    <div className="data-sources-page compact">
      <div className="page-header compact">
        <h1 className="page-title">Data Sources</h1>
      </div>

      <div className="data-sources-layout compact">
        {/* Left: Data Sources List */}
        <div className="sources-list-panel compact">
          <div className="list-header compact">
            <h2 className="panel-title">Connected Sources</h2>
            <span className="source-count">{dataSources.length}</span>
          </div>

          <div className="sources-table-container">
            {loading && dataSources.length === 0 ? (
              <SkeletonList rows={5} />
            ) : dataSources.length === 0 ? (
              <div className="empty-state compact">
                <div className="empty-icon">💾</div>
                <p>No data sources</p>
              </div>
            ) : (
              <div className="sources-grid compact">
                {dataSources.map((ds) => (
                  <div key={ds.id} className="source-card compact">
                    <div className="source-header compact">
                      <div className="source-icon compact">
                        {getDbIcon(ds.db_type)}
                      </div>
                      <div className="source-info compact">
                        <h3 className="source-name">{ds.name}</h3>
                        <span className="source-type">
                          {formatDbTypeLabel(ds.db_type)}
                        </span>
                      </div>
                    </div>

                    <div className="source-details compact">
                      <div className="detail-item compact">
                        <span className="detail-label">Host:</span>
                        <span className="detail-value">
                          {ds.host}
                          {ds.port ? `:${ds.port}` : ""}
                        </span>
                      </div>
                      <div className="detail-item compact">
                        <span className="detail-label">
                          {ds.db_type === "azure_devops" ? "Project:" : "DB:"}
                        </span>
                        <span className="detail-value">{ds.database}</span>
                      </div>
                    </div>

                    <div className="source-actions compact">
                      <button
                        onClick={() => handleTestConnection(ds.id)}
                        disabled={testingConnection === ds.id}
                        className="btn btn--outline btn--xs"
                        title="Test Connection"
                      >
                        {testingConnection === ds.id ? (
                          <div className="button-spinner"></div>
                        ) : (
                          "Test"
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right: Create Form */}
        <div className="create-form-panel compact">
          <div className="form-card compact">
            <h2 className="panel-title">New Data Source</h2>

            <form onSubmit={handleSubmit} className="data-source-form compact">
              <div className="form-grid compact">
                <div className="form-group compact">
                  <label className="form-label">Name</label>
                  <input
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    className="form-input compact"
                  />
                </div>

                <div className="form-group compact">
                  <label className="form-label">Type</label>
                  <select
                    name="db_type"
                    value={form.db_type}
                    onChange={handleChange}
                    className="form-select compact"
                  >
                    <option value="mysql">MySQL</option>
                    <option value="postgres">PostgreSQL</option>
                    <option value="sqlite">SQLite</option>
                    <option value="jira">Jira Cloud</option>
                    <option value="azure_devops">Azure DevOps</option>
                  </select>
                </div>

                <div className="form-group compact">
                  <label className="form-label">{hostLabel}</label>
                  <input
                    name="host"
                    value={form.host}
                    onChange={handleChange}
                    required={!isSqlite}
                    className="form-input compact"
                    placeholder={
                      isJira
                        ? "https://yourtenant.atlassian.net"
                        : isAdo
                        ? "your-org"
                        : "127.0.0.1"
                    }
                  />
                </div>

                <div className="form-group compact">
                  <label className="form-label">Port</label>
                  <input
                    name="port"
                    type="number"
                    value={form.port}
                    onChange={handleChange}
                    required={!isSqlite}
                    className="form-input compact"
                    placeholder={isSqlite ? "(unused)" : ""}
                  />
                </div>

                <div className="form-group compact">
                  <label className="form-label">{databaseLabel}</label>
                  <input
                    name="database"
                    value={form.database}
                    onChange={handleChange}
                    required
                    className="form-input compact"
                    placeholder={
                      isSqlite
                        ? "local.db"
                        : isAdo
                        ? "ProjectName"
                        : form.database
                    }
                  />
                </div>

                <div className="form-group compact">
                  <label className="form-label">{usernameLabel}</label>
                  <input
                    name="username"
                    value={form.username}
                    onChange={handleChange}
                    required={!isAdo} // ADO username is optional (PAT only)
                    className="form-input compact"
                  />
                </div>

                <div className="form-group compact">
                  <label className="form-label">{passwordLabel}</label>
                  <input
                    name="password"
                    type="password"
                    value={form.password}
                    onChange={handleChange}
                    required
                    className="form-input compact"
                  />
                </div>
              </div>

              <div className="form-actions compact">
                <button
                  type="submit"
                  disabled={loading}
                  className="btn btn--primary btn--xs"
                >
                  {loading ? (
                    <div className="button-spinner"></div>
                  ) : (
                    "Create"
                  )}
                </button>
              </div>
            </form>

            {message && (
              <div
                className={`message compact ${
                  message.includes("❌")
                    ? "message--error"
                    : "message--success"
                }`}
              >
                {message}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
